import { useCallback, useMemo } from 'react'

import { useAppSelector } from '@/store/hook'
import {
  selectAssignableCountries,
  selectAssignableCountryRolesList,
  selectAssignedRolesByUserId,
} from '@/store/selectors/roleSelector'
import { REGISTERED_USER_ROLE_NAME } from '@/libs/constants'
import { isValidArray } from '@/libs/utils/array'

function useAssignRoles(userId) {
  const assignableCountries = useAppSelector(selectAssignableCountries)
  const assignedRoles = useAppSelector(selectAssignedRolesByUserId(userId))
  const assignableCountryRolesList = useAppSelector(selectAssignableCountryRolesList)
  const firstCountryValue = isValidArray(assignableCountries) ? assignableCountries[0]?.value : undefined

  const findAssignableRoles = useCallback(
    (selectedCountry) => {
      if (selectedCountry) {
        const matchItem = assignableCountryRolesList.find(
          (item) => item.country === selectedCountry,
        )
        if (matchItem) {
          return matchItem?.roles
        }
      }
      return []
    },
    [assignableCountryRolesList],
  )

  const assignableRoles = useMemo(() => {
    return new Set(
      findAssignableRoles(firstCountryValue)?.map((role) => role.value),
    )
  }, [findAssignableRoles, firstCountryValue])

  const selectedRoles = useMemo(() => {
    if (isValidArray(assignedRoles)) {
      return assignedRoles
        .filter((assignedRole) => assignableRoles.has(assignedRole?.roleId))
        .map((assignedRole) => assignedRole?.roleId)
    }

    if (!userId) {
      const registeredUserRole = findAssignableRoles(firstCountryValue)
        .find((role) => role.label === REGISTERED_USER_ROLE_NAME)
      return registeredUserRole ? [registeredUserRole.value] : []
    }

    return []
  }, [assignedRoles, assignableRoles, userId, findAssignableRoles, firstCountryValue])

  const countryRoles = useMemo(() => {
    if (!isValidArray(assignableCountries)) {
      return []
    }

    return [
      {
        salesOrg: firstCountryValue,
        roles: selectedRoles,
      },
    ]
  }, [firstCountryValue, assignableCountries, selectedRoles])

  return {
    assignableCountryRolesList,
    countryRoles,
    assignedRoles,
    firstCountryValue,
    findAssignableRoles,
  }
}

export { useAssignRoles }
